import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dayDatesInclusive, formatYMD, nightDatesBetween, parseYMDToUTC } from '@baerenfang/shared';
import {
  addPollSlot,
  addPollSlotsBulk,
  assignPollClaim,
  deletePollClaim,
  deletePollSlot,
  patchPollSlot,
  type HutDuty,
  type PollDetail,
  type PollSlot,
  type PollSlotState,
} from '../../lib/api';
import {
  BTN_DANGER,
  BTN_ICON_DANGER,
  BTN_PRIMARY,
  BTN_SECONDARY,
  DAY_MS,
  INPUT,
  useErr,
} from './poll-ui';

type SlotDraft = {
  check_in: string;
  check_out: string;
  label: string;
  beds: string;
  duty: boolean;
};

export function SlotSection({
  detail,
  roster,
  onReload,
}: {
  detail: PollDetail;
  roster: HutDuty[];
  onReload: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const toMsg = useErr();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [label, setLabel] = useState('');
  const [state, setState] = useState<PollSlotState>('open');
  const [beds, setBeds] = useState('0');
  const [duty, setDuty] = useState(true);
  const [assignee, setAssignee] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // Bed-count drafts for the claimant chips, keyed by claim id.
  const [bedsDrafts, setBedsDrafts] = useState<Record<number, string>>({});
  // One slot is edited at a time; editDraft mirrors the add form's fields.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<SlotDraft | null>(null);

  // Series generator (bulk): every <weekday> in [bulkFrom, bulkTo], each
  // spanning <bulkNights> nights. Reuses the label/state/beds/duty controls above.
  const [bulkFrom, setBulkFrom] = useState('');
  const [bulkTo, setBulkTo] = useState('');
  const [bulkWeekday, setBulkWeekday] = useState(5); // Friday
  const [bulkNights, setBulkNights] = useState('2');

  const weekdayNames = useMemo(() => {
    // Index i == getUTCDay() i; 2023-01-01 is a Sunday (day 0).
    const base = Date.UTC(2023, 0, 1);
    const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: 'long', timeZone: 'UTC' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(base + i * DAY_MS)));
  }, [i18n.language]);

  const occurrences = useMemo(() => {
    if (!bulkFrom || !bulkTo) return [] as { check_in: string; check_out: string }[];
    const start = parseYMDToUTC(bulkFrom);
    const end = parseYMDToUTC(bulkTo);
    const nights = Math.max(1, Number.parseInt(bulkNights, 10) || 1);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
    const out: { check_in: string; check_out: string }[] = [];
    for (let ts = start; ts <= end; ts += DAY_MS) {
      if (new Date(ts).getUTCDay() === bulkWeekday) {
        out.push({ check_in: formatYMD(ts), check_out: formatYMD(ts + nights * DAY_MS) });
      }
    }
    return out;
  }, [bulkFrom, bulkTo, bulkWeekday, bulkNights]);

  const generateSeries = async () => {
    if (occurrences.length === 0) return;
    const nights = Math.max(1, Number.parseInt(bulkNights, 10) || 1);
    const bedsN = Number.parseInt(beds, 10) || 0;
    const slots = occurrences.map((o) => ({
      check_in: o.check_in,
      check_out: o.check_out,
      label: label || null,
      state,
      assignee_name_id: state === 'locked' && assignee ? Number(assignee) : null,
      beds_per_night: bedsN > 0 ? Array<number>(nights).fill(bedsN) : [],
      is_duty_per_day: duty ? Array<boolean>(nights + 1).fill(true) : [],
    }));
    try {
      await addPollSlotsBulk(detail.id, slots);
      setBulkFrom('');
      setBulkTo('');
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  const claimsBySlot = useMemo(() => {
    const m = new Map<number, PollDetail['claims']>();
    for (const cl of detail.claims.filter((c) => c.status === 'active')) {
      const l = m.get(cl.slot_id) ?? [];
      l.push(cl);
      m.set(cl.slot_id, l);
    }
    return m;
  }, [detail.claims]);

  const buildArrays = (ci: string, co: string) => {
    const nights = nightDatesBetween(ci, co).length;
    const days = dayDatesInclusive(ci, co).length;
    const bedsN = Number.parseInt(beds, 10) || 0;
    return {
      beds_per_night: bedsN > 0 ? Array(nights).fill(bedsN) : [],
      is_duty_per_day: duty ? Array(days).fill(true) : [],
    };
  };

  const add = async () => {
    if (!checkIn || !checkOut) return;
    try {
      const arrays = buildArrays(checkIn, checkOut);
      await addPollSlot(detail.id, {
        check_in: checkIn,
        check_out: checkOut,
        label: label || null,
        state,
        assignee_name_id: state === 'locked' && assignee ? Number(assignee) : null,
        ...arrays,
      });
      setCheckIn('');
      setCheckOut('');
      setLabel('');
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  const remove = async (slotId: number) => {
    try {
      await deletePollSlot(detail.id, slotId);
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  // Put a participant on an open slot ahead of the poll. This is an ordinary
  // claim: the participant sees it as their own and anyone else can still claim
  // the same slot, which finalize then reports as contested. Beds are left to
  // the server, which inherits the slot's own per-night template.
  const assign = async (slotId: number, participantId: number) => {
    try {
      await assignPollClaim(detail.id, { slot_id: slotId, participant_id: participantId });
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  const removeClaim = async (claim: PollDetail['claims'][number]) => {
    if (!window.confirm(t('poll.admin.confirmRemoveClaim', { name: claim.participant_name }))) {
      return;
    }
    try {
      await deletePollClaim(detail.id, claim.id);
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  // Correct how many beds someone needs, on their chip. The same upsert handles
  // create and update, so this needs no extra endpoint -- but it replaces the
  // whole row, so `rank` has to be echoed back: PollAdminClaimInput defaults it
  // to 1, which would silently demote a participant's own 2. Wahl to 1. Wahl.
  const saveBeds = async (claim: PollDetail['claims'][number]) => {
    const draft = bedsDrafts[claim.id];
    if (draft === undefined) return;
    const n = Math.min(1000, Math.max(0, Number.parseInt(draft, 10) || 0));
    setBedsDrafts((d) => {
      const next = { ...d };
      delete next[claim.id];
      return next;
    });
    if (n === claim.beds) return;
    try {
      await assignPollClaim(detail.id, {
        slot_id: claim.slot_id,
        participant_id: claim.participant_id,
        rank: claim.rank,
        beds: n,
      });
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  const startEdit = (s: PollSlot) => {
    setEditingId(s.id);
    setEditDraft({
      check_in: s.check_in,
      check_out: s.check_out,
      label: s.label ?? '',
      // The add form and the series generator only ever write uniform templates,
      // so one number round-trips every slot the UI can create.
      beds: String(s.beds_per_night[0] ?? 0),
      duty: s.is_duty_per_day.length > 0,
    });
  };

  const saveEdit = async (s: PollSlot) => {
    if (!editDraft || !editDraft.check_in || !editDraft.check_out) return;
    const bedsN = Math.min(1000, Math.max(0, Number.parseInt(editDraft.beds, 10) || 0));
    const nights = nightDatesBetween(editDraft.check_in, editDraft.check_out).length;
    const days = dayDatesInclusive(editDraft.check_in, editDraft.check_out).length;
    try {
      // PATCH replaces the whole slot and PollSlotInput has defaults: omitting
      // `state` would turn a Fixiert/Geschlossen slot into Frei, and omitting
      // assignee_name_id would clear it. Echo both unchanged -- this screen only
      // edits dates, label and the bed template.
      await patchPollSlot(detail.id, s.id, {
        check_in: editDraft.check_in,
        check_out: editDraft.check_out,
        label: editDraft.label || null,
        state: s.state,
        assignee_name_id: s.assignee_name_id,
        beds_per_night: bedsN > 0 ? Array<number>(nights).fill(bedsN) : [],
        is_duty_per_day: editDraft.duty ? Array<boolean>(days).fill(true) : [],
      });
      setEditingId(null);
      setEditDraft(null);
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  const stateBadge = (s: PollSlot) => {
    if (s.state === 'closed')
      return <span className="text-slate-500">🧹 {t('poll.state.closed')}</span>;
    if (s.state === 'locked')
      return (
        <span className="text-slate-700 dark:text-slate-300">🔒 {t('poll.state.locked')}</span>
      );
    const n = claimsBySlot.get(s.id)?.length ?? 0;
    if (n === 0) return <span className="text-slate-400">{t('poll.admin.noClaims')}</span>;
    if (n === 1) return <span className="text-emerald-600">✅ 1</span>;
    return <span className="text-amber-600">⚠ {n}</span>;
  };

  return (
    <section className="space-y-2">
      <h3 className="font-semibold">{t('poll.admin.slots')}</h3>
      {error && (
        <p role="alert" className="text-sm text-rose-700">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 dark:border-slate-700 p-3">
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.from')}
          <input
            type="date"
            className={INPUT}
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.to')}
          <input
            type="date"
            className={INPUT}
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.slotLabel')}
          <input
            className={INPUT}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('poll.admin.slotLabelPlaceholder')}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.state')}
          <select
            className={INPUT}
            value={state}
            onChange={(e) => setState(e.target.value as PollSlotState)}
          >
            <option value="open">{t('poll.state.open')}</option>
            <option value="locked">{t('poll.state.locked')}</option>
            <option value="closed">{t('poll.state.closed')}</option>
          </select>
        </label>
        {state === 'locked' && (
          <label className="flex flex-col text-xs text-slate-500">
            {t('poll.admin.assignee')}
            <select
              className={INPUT}
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            >
              <option value="">—</option>
              {roster.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.beds')}
          <input
            className={`${INPUT} w-20`}
            inputMode="numeric"
            value={beds}
            onChange={(e) => setBeds(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 pb-2">
          <input type="checkbox" checked={duty} onChange={(e) => setDuty(e.target.checked)} />
          {t('poll.admin.dutyMarker')}
        </label>
        <button type="button" className={BTN_PRIMARY} onClick={() => void add()}>
          {t('poll.admin.addSlot')}
        </button>
      </div>

      {/* Series generator — reuses the label/state/beds/duty controls above */}
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-slate-300 dark:border-slate-600 p-3">
        <span className="w-full text-xs font-medium text-slate-500">
          {t('poll.admin.bulkTitle')}
        </span>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.from')}
          <input
            type="date"
            className={INPUT}
            value={bulkFrom}
            onChange={(e) => setBulkFrom(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.to')}
          <input
            type="date"
            className={INPUT}
            value={bulkTo}
            onChange={(e) => setBulkTo(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.bulkWeekday')}
          <select
            className={INPUT}
            value={bulkWeekday}
            onChange={(e) => setBulkWeekday(Number(e.target.value))}
          >
            {weekdayNames.map((name, i) => (
              <option key={i} value={i}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.bulkNights')}
          <input
            className={`${INPUT} w-20`}
            inputMode="numeric"
            value={bulkNights}
            onChange={(e) => setBulkNights(e.target.value)}
          />
        </label>
        <button
          type="button"
          className={BTN_PRIMARY}
          disabled={occurrences.length === 0}
          onClick={() => void generateSeries()}
        >
          {t('poll.admin.bulkGenerate', { n: occurrences.length })}
        </button>
        <span className="text-xs text-slate-500 pb-2">
          {t('poll.admin.bulkHint', { beds: Number.parseInt(beds, 10) || 0 })}
        </span>
      </div>

      <ul className="divide-y divide-slate-200 dark:divide-slate-700 rounded-md border border-slate-200 dark:border-slate-700">
        {detail.slots.map((s) => {
          const claims = claimsBySlot.get(s.id) ?? [];
          const claimed = new Set(claims.map((cl) => cl.participant_id));
          return (
            <li key={s.id} className="px-3 py-2 text-sm">
              {editingId === s.id && editDraft ? (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col text-xs text-slate-500">
                    {t('poll.admin.from')}
                    <input
                      type="date"
                      className={INPUT}
                      value={editDraft.check_in}
                      onChange={(e) => setEditDraft((d) => d && { ...d, check_in: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col text-xs text-slate-500">
                    {t('poll.admin.to')}
                    <input
                      type="date"
                      className={INPUT}
                      value={editDraft.check_out}
                      onChange={(e) =>
                        setEditDraft((d) => d && { ...d, check_out: e.target.value })
                      }
                    />
                  </label>
                  <label className="flex flex-col text-xs text-slate-500">
                    {t('poll.admin.slotLabel')}
                    <input
                      className={INPUT}
                      value={editDraft.label}
                      onChange={(e) => setEditDraft((d) => d && { ...d, label: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col text-xs text-slate-500">
                    {t('poll.admin.beds')}
                    <input
                      className={`${INPUT} w-20`}
                      inputMode="numeric"
                      value={editDraft.beds}
                      onChange={(e) => setEditDraft((d) => d && { ...d, beds: e.target.value })}
                    />
                  </label>
                  <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 pb-2">
                    <input
                      type="checkbox"
                      checked={editDraft.duty}
                      onChange={(e) => setEditDraft((d) => d && { ...d, duty: e.target.checked })}
                    />
                    {t('poll.admin.dutyMarker')}
                  </label>
                  {/* state and assignee are not editable here, but they are sent
                      back unchanged -- see saveEdit. */}
                  <span className="pb-2">{stateBadge(s)}</span>
                  <button type="button" className={BTN_PRIMARY} onClick={() => void saveEdit(s)}>
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    className={BTN_SECONDARY}
                    onClick={() => {
                      setEditingId(null);
                      setEditDraft(null);
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="tabular-nums">
                    {s.check_in} → {s.check_out}
                  </span>
                  {s.label && <span className="text-slate-500">· {s.label}</span>}
                  <span className="ml-auto">{stateBadge(s)}</span>
                  {(s.comment_count ?? 0) > 0 && (
                    <span className="text-slate-400">💬{s.comment_count}</span>
                  )}
                  {detail.status !== 'finalized' && (
                    <button type="button" className={BTN_SECONDARY} onClick={() => startEdit(s)}>
                      {t('poll.admin.editSlot')}
                    </button>
                  )}
                  <button type="button" className={BTN_DANGER} onClick={() => void remove(s.id)}>
                    {t('common.delete')}
                  </button>
                </div>
              )}

              {/* Only open slots take claims: locked slots run off
                  assignee_name_id and closed ones are event blocks. */}
              {s.state === 'open' && (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {claims.map((cl) => (
                    <span
                      key={cl.id}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 pl-2 pr-1 py-0.5 text-xs"
                    >
                      {cl.participant_name}
                      <span className="text-slate-400">·</span>
                      <input
                        className={`${INPUT} w-12 px-1 py-0 text-xs`}
                        inputMode="numeric"
                        aria-label={t('poll.admin.bedsForName', { name: cl.participant_name })}
                        title={t('poll.admin.bedsForName', { name: cl.participant_name })}
                        value={bedsDrafts[cl.id] ?? String(cl.beds)}
                        onChange={(e) => setBedsDrafts((d) => ({ ...d, [cl.id]: e.target.value }))}
                        onBlur={() => void saveBeds(cl)}
                      />
                      <button
                        type="button"
                        className={BTN_ICON_DANGER}
                        title={t('poll.admin.removeClaim')}
                        aria-label={t('poll.admin.removeClaimName', {
                          name: cl.participant_name,
                        })}
                        onClick={() => void removeClaim(cl)}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  <select
                    className={`${INPUT} py-1 text-xs`}
                    value=""
                    onChange={(e) => {
                      if (e.target.value) void assign(s.id, Number(e.target.value));
                    }}
                  >
                    <option value="">{t('poll.admin.addClaim')}</option>
                    {detail.participants
                      .filter((p) => !claimed.has(p.id))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {/* No roster link -> finalize would reject the slot as
                              participant_not_on_roster, so flag it up front. */}
                          {p.name_id == null ? '⚠ ' : ''}
                          {p.resolved_name ?? t('poll.admin.unnamed')}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </li>
          );
        })}
        {detail.slots.length === 0 && (
          <li className="px-3 py-2 text-sm text-slate-500">{t('poll.admin.noSlots')}</li>
        )}
      </ul>
    </section>
  );
}
